import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  inject,
  input,
  output,
  ChangeDetectionStrategy,
} from "@angular/core";
import { Package } from "./package.model";
import { LclQuote } from "../../models/quote-lcl.model";
import { PackageMapper } from "./package.mapper";
import { PackageRepository } from "./package.repository";
import { Subscription, Subject } from "rxjs";
import { debounceTime } from "rxjs/operators";
import { NotifyService } from "app/notify/notify.service";
import { TOAST } from "app/notify/toast/toast-type.enum";
import { BackendErrorPipe } from "app/shared/backend/backend-error.pipe";
import currency from "currency.js";
import { unitType } from "./unitType.const";
import { LtlQuote } from "app/quote/models/quote-ltl.model";
import { AirQuote } from "app/quote/models/quote-air.model";
import { Bind } from "primeng/bind";
import { ButtonDirective } from "primeng/button";
import { Popover } from "primeng/popover";
import { PopoverArrowDirective } from "app/ui/popover/popover-arrow.directive";
import { Tooltip } from "primeng/tooltip";
import { FormsModule } from "@angular/forms";
import { InputText } from "primeng/inputtext";
import { Checkbox } from "primeng/checkbox";
import { SelectButton } from "primeng/selectbutton";
import { CurrencyPipe } from "../../../shared/currencies/currency.pipe";
import { Quote } from "../../quote.type";
import { PtlQuote } from "../../models/quote-ptl.model";

export interface IPackageApplyEvent {
  weight: number;
  measurement: number;
  pieces: number;
  ldm: number;
  packages: Package[];
}

@Component({
  selector: "app-quote-package",
  templateUrl: "./quote-package.component.html",
  styleUrls: ["./quote-package.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ButtonDirective,
    Bind,
    Popover,
    PopoverArrowDirective,
    Tooltip,
    FormsModule,
    InputText,
    Checkbox,
    SelectButton,
    CurrencyPipe,
  ],
})
export class QuotePackageComponent implements OnInit, OnChanges, OnDestroy {
  private packageRepository = inject(PackageRepository);
  private notifyService = inject(NotifyService);

  @Input() quote: LclQuote | LtlQuote | AirQuote;
  readonly showLdm = input(false);
  readonly showPallets = input(false);
  readonly showCentimeters = input(false);
  readonly disabled = input<boolean>(false);

  readonly apply = output<IPackageApplyEvent>();
  readonly refreshQuote = output<Quote>();

  public packages: Package[] = [];
  public measurementUnit: string;
  public weightUnit: string;
  public cubicUnit: string;
  public loading: boolean = false;
  public packageSubject = new Subject<Package>();
  public unitOptions: any[] = [
    { label: "kg", value: unitType.metric },
    { label: "lbs", value: unitType.imperial },
  ];
  public selectedUnit: string = unitType.metric;

  protected subscription = new Subscription();
  private cmToIn = 2.54;
  private kgToLbs = 0.45;
  private cbmToCbf = 35.3;

  ngOnInit() {
    this.mapQuotePackages();
    this.updateCentimetersFromMeters();
    this.measurementUnit = this.showCentimeters() ? "cm" : "m";
    this.weightUnit = "kg";
    this.cubicUnit = "CBM";
    this.packageSubject.pipe(debounceTime(300)).subscribe((item) => {
      this.updateLdmAndMeasurementRequest(item);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.quote) {
      this.quote = changes.quote.currentValue;
      this.mapQuotePackages();
      this.updateCentimetersFromMeters();
    }
  }

  public onQuoteAi(quote: LclQuote) {
    this.quote = quote;
    this.mapQuotePackages();
    this.updateCentimetersFromMeters();
    this.refreshQuote.emit(quote);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  public getTotalQuantity(): number {
    return this.packages.reduce((sum, itm) => {
      return sum + itm.quantity;
    }, 0);
  }

  public getTotalLdm(): number {
    return (
      this.packages.reduce((sum, itm) => {
        return sum + itm.ldm;
      }, 0) || 0
    );
  }

  public getMeasurementSum(): number {
    return (
      this.packages.reduce((sum, itm) => {
        return sum + itm.measurement;
      }, 0) || 0
    );
  }

  public getWeightSum(): number {
    return (
      this.packages.reduce((sum, itm) => {
        return sum + itm.quantity * itm.weight;
      }, 0) || 0
    );
  }

  public isPtlQuote() {
    return this.quote instanceof PtlQuote;
  }

  public hasPackages(): boolean {
    return this.quote.getPackages().length > 0;
  }

  public getButtonLabel(): string {
    return this.hasPackages() ? "Edit packages" : "Packages";
  }

  public togglePanel(event: Event, panel: Popover, target?: HTMLElement) {
    if (this.packages.length === 0) {
      this.addPackage();
    }
    panel.toggle(event, target);
  }

  public addPackage() {
    let isMeasurementRequired = true;
    if (this.isPtlQuote()) {
      isMeasurementRequired = false;
    }
    this.packages.push(
      new Package(1, 0, 0, 0, 0, 0, 0, true, false, isMeasurementRequired),
    );
  }

  public removePackage(idx: number) {
    this.packages.splice(idx, 1);
  }

  public applyEmit() {
    if (!this.areAllPackagesValid()) {
      return;
    }

    this.apply.emit({
      weight: this.getWeightSum(),
      measurement: this.getMeasurementSum(),
      pieces: this.getTotalQuantity(),
      ldm: this.getTotalLdm(),
      packages: this.packages,
    });
  }

  public updateMetersFromCentimeters(item: Package): void {
    if (this.showCentimeters() && this.selectedUnit == unitType.metric) {
      item.length = currency(item.lengthInUnit, { precision: 4 }).divide(
        100,
      ).value;
      item.width = currency(item.widthInUnit, { precision: 4 }).divide(
        100,
      ).value;
      item.height = currency(item.heightInUnit, { precision: 4 }).divide(
        100,
      ).value;
      item.weight = item.weightInUnit;
    } else if (this.selectedUnit == unitType.metric) {
      item.length = item.lengthInUnit;
      item.width = item.widthInUnit;
      item.height = item.heightInUnit;
      item.weight = item.weightInUnit;
    } else if (this.selectedUnit == unitType.imperial) {
      item.length = currency(item.lengthInUnit, { precision: 4 })
        .multiply(this.cmToIn)
        .divide(100).value;
      item.width = currency(item.widthInUnit, { precision: 4 })
        .multiply(this.cmToIn)
        .divide(100).value;
      item.height = currency(item.heightInUnit, { precision: 4 })
        .multiply(this.cmToIn)
        .divide(100).value;
      item.weight = currency(item.weightInUnit, { precision: 4 }).multiply(
        this.kgToLbs,
      ).value;
    }
  }

  public updateCentimetersFromMeters(): void {
    this.packages.forEach((item) => {
      if (this.showCentimeters() && this.selectedUnit == unitType.metric) {
        item.lengthInUnit = currency(item.length, { precision: 2 }).multiply(
          100,
        ).value;
        item.widthInUnit = currency(item.width, { precision: 2 }).multiply(
          100,
        ).value;
        item.heightInUnit = currency(item.height, { precision: 2 }).multiply(
          100,
        ).value;
        item.weightInUnit = item.weight;
      } else if (this.selectedUnit == unitType.metric) {
        item.lengthInUnit = item.length;
        item.widthInUnit = item.width;
        item.heightInUnit = item.height;
        item.weightInUnit = item.weight;
      } else if (this.selectedUnit == unitType.imperial) {
        item.lengthInUnit = currency(item.length, { precision: 4 })
          .divide(this.cmToIn)
          .multiply(100).value;
        item.widthInUnit = currency(item.width, { precision: 4 })
          .divide(this.cmToIn)
          .multiply(100).value;
        item.heightInUnit = currency(item.height, { precision: 4 })
          .divide(this.cmToIn)
          .multiply(100).value;
        item.weightInUnit = currency(item.weight, { precision: 4 }).divide(
          this.kgToLbs,
        ).value;
      }
    });
  }

  public updateStackable(item: Package): void {
    if (item.height > 1.1) {
      item.stackable = false;
      item.stackableDisabled = true;
    } else {
      item.stackableDisabled = false;
    }
  }

  public areAllPackagesValid(): boolean {
    return this.packages.every((p) => p.isValid());
  }

  public arePackagesModified(): boolean {
    return !this.arePackageArraysEqual(this.quote.getPackages(), this.packages);
  }

  private arePackageArraysEqual(a: Package[], b: Package[]): boolean {
    if (a.length !== b.length) {
      return false;
    }
    return a.every((pkg, index) => {
      const current = pkg as unknown as Record<string, unknown>;
      const other = b[index] as unknown as Record<string, unknown>;
      const keys = Object.keys(current);
      return (
        keys.length === Object.keys(other).length &&
        keys.every((key) => current[key] === other[key])
      );
    });
  }

  public updateLdmAndMeasurement(item: Package) {
    item.loading = true;
    this.packageSubject.next(item);
  }

  public isLoading(): boolean {
    for (let i = 0; i < this.packages.length; i++) {
      if (this.packages[i].loading) {
        return true;
      }
    }

    return false;
  }

  public updateUnits(unit: string): void {
    if (this.selectedUnit == unit || !unit) {
      return;
    }
    this.selectedUnit = unit;

    if (this.selectedUnit == unitType.metric) {
      this.measurementUnit = this.showCentimeters() ? "cm" : "m";
      this.weightUnit = "kg";
      this.cubicUnit = "CBM";
    } else {
      this.measurementUnit = "in";
      this.weightUnit = "lbs";
      this.cubicUnit = "CBF";
    }
    this.packages.forEach((item) => {
      if (this.selectedUnit == unitType.metric) {
        item.lengthInUnit = currency(item.lengthInUnit, {
          precision: 2,
        }).multiply(this.cmToIn).value;
        item.widthInUnit = currency(item.widthInUnit, {
          precision: 2,
        }).multiply(this.cmToIn).value;
        item.heightInUnit = currency(item.heightInUnit, {
          precision: 2,
        }).multiply(this.cmToIn).value;
        item.weightInUnit = currency(item.weightInUnit, {
          precision: 2,
        }).multiply(this.kgToLbs).value;
      } else {
        item.lengthInUnit = currency(item.lengthInUnit, {
          precision: 2,
        }).divide(this.cmToIn).value;
        item.widthInUnit = currency(item.widthInUnit, { precision: 2 }).divide(
          this.cmToIn,
        ).value;
        item.heightInUnit = currency(item.heightInUnit, {
          precision: 2,
        }).divide(this.cmToIn).value;
        item.weightInUnit = currency(item.weightInUnit, {
          precision: 2,
        }).divide(this.kgToLbs).value;
      }
    });
  }

  public getMeasurementInUnit(measurement): number {
    if (this.selectedUnit == unitType.metric) {
      return measurement;
    } else {
      return currency(measurement, { precision: 2 }).multiply(this.cbmToCbf)
        .value;
    }
  }

  private updateLdmAndMeasurementRequest(item: Package) {
    this.subscription.add(
      this.packageRepository.calculate(item).subscribe({
        next: (response): void => {
          item.loading = false;
          item.setLdm(response.getLdm());
          item.setMeasurement(response.getMeasurement());
        },
        error: (err): void => {
          this.loading = false;
          this.notifyService.toast(
            TOAST.ERROR,
            "Can't update package",
            new BackendErrorPipe().transform(err, "string"),
          );
        },
      }),
    );
  }

  private mapQuotePackages() {
    this.packages = this.quote
      .getPackages()
      .map((pkg) => PackageMapper.map(pkg, !this.isPtlQuote()));
    for (let i = 0; i < this.packages.length; i++) {
      this.updateStackable(this.packages[i]);
    }
  }
}
